/**
 * Local Theme Definition (v1 structure)
 *
 * This file replicates the theme structure from @healingpaper/cell
 * to remove external dependency while maintaining compatibility.
 *
 * DO NOT import this from external packages.
 * Values are hardcoded based on v1 design system.
 */

// ============================================================
// Color Definitions
// ============================================================

const grayScale = {
  gray50: '#FCFCFC',
  gray100: '#FAFAFA',
  gray200: '#F5F5F5',
  gray300: '#ECECEC',
  gray400: '#D6D6D6',
  gray500: '#BCBCBC',
  gray600: '#A3A3A3',
  gray700: '#858585',
  gray800: '#666666',
  gray900: '#222222',
}

const redScale = {
  red50: '#FEF4F4',
  red100: '#FEEAED',
  red200: '#FFD1D6',
  red300: '#FF9191',
  red400: '#EF696A',
  red500: '#EF3B32',
  red600: '#E03131',
  red700: '#CE272B',
  red800: '#C11F24',
  red900: '#B21018',
}

const orangeScale = {
  orange50: '#FFF6F1',
  orange100: '#FFEDE3',
  orange200: '#FFCBB9',
  orange300: '#FFA98C',
  orange400: '#FF6D38',
  orange500: '#FF540F',
  orange600: '#F44D0A',
  orange700: '#E64604',
  orange800: '#D83E00',
  orange900: '#C03000',
}

const yellowScale = {
  yellow50: '#FFFAEC',
  yellow100: '#FFF7E0',
  yellow200: '#FFEDBD',
  yellow300: '#FFD267',
  yellow400: '#FFBB3D',
  yellow500: '#E19D48',
  yellow600: '#BF8650',
  yellow700: '#9E6C36',
  yellow800: '#7E541C',
  yellow900: '#613D01',
}

const greenScale = {
  green50: '#F2FAF2',
  green100: '#E5F5E6',
  green200: '#C0E6C3',
  green300: '#97D59C',
  green400: '#6AC674',
  green500: '#43BA55',
  green600: '#01AD34',
  green700: '#009923',
  green800: '#007B13',
  green900: '#005D00',
}

const tealScale = {
  teal50: '#EFFAFE',
  teal100: '#E0F5FD',
  teal200: '#B2E7F9',
  teal300: '#80D7F4',
  teal400: '#4CC7ED',
  teal500: '#0BB1DE',
  teal600: '#00A0CA',
  teal700: '#008EAF',
  teal800: '#007A96',
  teal900: '#00596A',
}

const blueScale = {
  blue50: '#F4F5FF',
  blue100: '#E9ECFF',
  blue200: '#D0D5FF',
  blue300: '#9EABF9',
  blue400: '#728AF7',
  blue500: '#4C6EF5',
  blue600: '#274DE5',
  blue700: '#2239CD',
  blue800: '#1E33A9',
  blue900: '#132273',
}

/**
 * @deprecated Use colors from semanticColors, grayScale, redScale, orangeScale, yellowScale, greenScale, tealScale, blueScale instead
 */
const legacySemanticColor = {
  /**
   * @deprecated Use normalSecondary instead
   */
  yellow: yellowScale.yellow300,
  /**
   * @deprecated Use lightSecondary instead
   */
  lightYellow: yellowScale.yellow100,
  /**
   * @deprecated Use darkSecondary instead
   */
  latte: yellowScale.yellow600,
  /**
   * @deprecated Use normalPrimary instead
   */
  orange: orangeScale.orange500,
  /**
   * @deprecated Use lightPrimary instead
   */
  lightOrange: '#FFEDE3',
  /**
   * @deprecated Use error instead
   */
  ban: redScale.red600,
}

const semanticColor = {
  black: '#000000',
  white: '#FFFFFF',
  dimmer: 'rgba(0, 0, 0, 0.60)',
  title: grayScale.gray900,
  description: grayScale.gray700,
  icon: grayScale.gray800,
  divider: '#ECECEC',
  imageBorder: 'rgba(0, 0, 0, 0.05)',
  darkReservation: greenScale.green600,
  lightReservation: greenScale.green50,
  darkPurchase: tealScale.teal600,
  lightPurchase: tealScale.teal50,
  lightPrimary: orangeScale.orange100,
  normalPrimary: orangeScale.orange500,
  lightSecondary: yellowScale.yellow100,
  normalSecondary: yellowScale.yellow300,
  darkSecondary: yellowScale.yellow600,
  certified: blueScale.blue500,
  warning: yellowScale.yellow400,
  success: greenScale.green600,
  error: redScale.red600,
  info: blueScale.blue600,
}

const colors = {
  ...grayScale,
  ...redScale,
  ...orangeScale,
  ...yellowScale,
  ...greenScale,
  ...tealScale,
  ...blueScale,
  ...legacySemanticColor,
  ...semanticColor,
}

export type Colors = typeof colors
export type ColorType = keyof Colors

// ============================================================
// Typography Definitions
// ============================================================

const title1 = `
  font-size: 26px;
  font-weight: bold;
  line-height: 36px;
  letter-spacing : -0.4px;
`

const title2 = `
  font-size: 20px;
  font-weight: bold;
  line-height: 28px;
  letter-spacing : -0.4px;
`

const title3 = `
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
  letter-spacing : -0.2px;
`

const title4 = `
  font-size: 14px;
  font-weight: bold;
  line-height: 20px;
  letter-spacing : -0.1px;
`

const subTitle0 = `
  font-size : 20px;
  font-weight : 500;
  line-height : 28px;
  letter-spacing : -0.4px;
`

const subTitle1 = `
  font-size : 16px;
  font-weight : 500;
  line-height : 24px;
  letter-spacing : -0.2px;
`

const subTitle2 = `
  font-size : 14px;
  font-weight : 500;
  line-height : 20px;
  letter-spacing : -0.1px;
`

const description1 = `
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing : -0.2px;
`

const description2 = `
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing : -0.1px;
`

const caption1 = `
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0;
`

const caption2 = `
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
`

const caption3 = `
  font-size: 11px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0;
`

const typography = {
  title1,
  title2,
  title3,
  title4,
  subTitle0,
  subTitle1,
  subTitle2,
  description1,
  description2,
  caption1,
  caption2,
  caption3,
}

export type Typography = typeof typography
export type TypographyType = keyof Typography

// ============================================================
// Theme Export
// ============================================================

export interface CellTheme {
  colors: Colors
  typography: Typography
}

export const theme: CellTheme = {
  colors,
  typography,
}

export default theme
